import React, { useState, useEffect, useRef, useCallback } from 'react';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { useNavigate } from 'react-router-dom';
import { getNotifications, getUnreadCount, markNotificationRead, markAllNotificationsRead } from '../services/api';
import { Bell, CheckCheck, Navigation, Package, Euro, X, KeyRound, ThumbsUp, ThumbsDown, AlertCircle, FileEdit, MessageCircle, PartyPopper } from 'lucide-react';

const POLL_INTERVAL = 30000; // 30 seconds

const typeIcons = {
  driver_on_the_way: Navigation,
  delivery_completed: Package,
  delivery_code: KeyRound,
  new_offer: Euro,
  first_offer: PartyPopper,
  offer_accepted: ThumbsUp,
  offer_rejected: ThumbsDown,
  delivery_cancelled: AlertCircle,
  delivery_edited: FileEdit,
  new_job_available: Package,
  new_review: ThumbsUp,
  support_message: MessageCircle,
};

const typeColors = {
  driver_on_the_way: 'bg-violet-100 text-violet-600',
  delivery_completed: 'bg-emerald-100 text-emerald-600',
  delivery_code: 'bg-amber-100 text-amber-600',
  new_offer: 'bg-blue-100 text-blue-600',
  first_offer: 'bg-emerald-100 text-emerald-700',
  offer_accepted: 'bg-emerald-100 text-emerald-600',
  offer_rejected: 'bg-gray-100 text-gray-600',
  delivery_cancelled: 'bg-red-100 text-red-600',
  delivery_edited: 'bg-amber-100 text-amber-600',
  new_job_available: 'bg-emerald-100 text-emerald-600',
  new_review: 'bg-yellow-100 text-yellow-600',
  support_message: 'bg-blue-100 text-blue-600',
};

export function NotificationBell() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const prevCountRef = useRef(0);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await getUnreadCount();
      const newCount = res.data.count;

      // Browser notification for NEW notifications
      if (newCount > prevCountRef.current && prevCountRef.current >= 0) {
        triggerBrowserNotification();
      }
      prevCountRef.current = newCount;
      setUnreadCount(newCount);
    } catch {
      // silently fail
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getNotifications();
      setNotifications(res.data);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  // Note: we used to auto-request Notification.requestPermission() here, but
  // that ONLY grants OS-level permission — it doesn't create the Web Push
  // subscription needed for the backend to actually send pushes. The
  // <NotificationPrompt /> banner now owns that flow (permission +
  // pushManager.subscribe + backend POST /push/subscribe).

  // 30-second background refresh of the unread count. Auto-pauses when the
  // tab is hidden (via useAutoRefresh's Page-Visibility hook) so mobile
  // battery isn't drained. First fire is manual so the bell renders promptly.
  useEffect(() => { fetchUnreadCount(); }, [fetchUnreadCount]);
  useAutoRefresh(fetchUnreadCount, POLL_INTERVAL);

  // Fetch full list when dropdown opens
  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, fetchNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const triggerBrowserNotification = () => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('JustDeliver', {
        body: 'You have a new notification!',
        icon: '/logo.png',
      });
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // silently fail
    }
  };

  const handleNotificationClick = async (notif) => {
    // Mark as read if needed
    if (!notif.read) {
      try {
        await markNotificationRead(notif.id);
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch {
        /* silent */
      }
    }
    // Navigate to the target URL if present
    if (notif.url) {
      setOpen(false);
      // Same-origin relative path → use react-router for SPA navigation
      if (notif.url.startsWith('/')) navigate(notif.url);
      else window.location.href = notif.url;
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      // silently fail
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors rounded-sm"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" strokeWidth={1.5} />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full px-1 animate-pulse"
            data-testid="notification-badge"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-x-2 top-16 sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 bg-white border border-gray-200 shadow-lg z-50 max-h-[480px] overflow-hidden flex flex-col" data-testid="notification-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="font-semibold text-sm">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-[#2E7D32] hover:text-[#1B5E20] font-medium flex items-center gap-1"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck className="w-3.5 h-3.5" /> Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Notification List */}
          <div className="overflow-y-auto flex-1">
            {loading && notifications.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Bell className="w-5 h-5 text-gray-300 animate-pulse" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                <Bell className="w-8 h-8 mb-2" strokeWidth={1} />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const Icon = typeIcons[notif.type] || Bell;
                const colorClass = typeColors[notif.type] || 'bg-gray-100 text-gray-600';
                return (
                  <div
                    key={notif.id}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-gray-50 transition-colors cursor-pointer hover:bg-gray-50 ${
                      !notif.read ? 'bg-blue-50/40' : ''
                    }`}
                    onClick={() => handleNotificationClick(notif)}
                    data-testid={`notification-item-${notif.id}`}
                  >
                    <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5 ${colorClass}`}>
                      <Icon className="w-4 h-4" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm ${!notif.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                          {notif.title}
                        </p>
                        {!notif.read && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notif.message}</p>
                      <p className="text-[10px] text-gray-400 mt-1">{formatTime(notif.created_at)}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
